import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  MapPin,
  Tag,
  Heart,
  Users,
  CaretRight,
  Percent,
} from "@phosphor-icons/react";
import { getCurrentLanguage } from "@shared/lib/i18n";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { PromotionsSlider } from "@shared/components/PromotionsSlider";
import { PWAInstallBanner } from "@shared/components/PWAInstallBanner";
import { getStoredUserSession } from "@shared/hooks/useUserSession";

export const Route = createFileRoute("/app/")({
  component: AppDashboard,
});

function AppDashboard() {
  const { t } = useTranslation();
  const currentLang = getCurrentLanguage();

  // Obține ofertele promovate pentru slider
  const promotedOffers = useQuery(api.offers.getPromotedOffers, { limit: 10 });
  
  // Obține ultimele oferte adăugate
  const latestOffers = useQuery(api.offers.getLatestOffers, { limit: 5 });

  // Obține user pentru badge-ul de invitații
  const { userId } = getStoredUserSession();
  const user = useQuery(
    api.auth.getCurrentUser,
    userId ? { userId: userId as any } : "skip"
  );

  return (
    <div className="pt-[80px] pb-[85px]">
      {/* Quick actions */}
      <div className="px-4 mt-6">
        <div className="grid grid-cols-4 gap-3">
          <QuickAction
            icon={<Tag size={20} />}
            label={t("nav.offers")}
            to="/app/offers"
          />
          <QuickAction
            icon={<MapPin size={20} />}
            label={t("nav.map")}
            to="/app/map"
          />
          <QuickAction
            icon={<Heart size={20} />}
            label={t("nav.favorites")}
            to="/app/favorites"
          />
          <QuickAction
            icon={<Users size={20} />}
            label={t("nav.invites")}
            to="/app/invites"
            badge={user?.invitesRemaining}
          />
        </div>
      </div>
      {/* Promotions Slider */}
      {promotedOffers && promotedOffers.length > 0 && (
        <div className="px-4 mt-8">
          <div className="flex items-center justify-between mb-4">
            <h5 className="text-lg font-medium text-text">
              {t("promotions")}
            </h5>
          </div>
          <PromotionsSlider offers={promotedOffers} currentLang={currentLang} />
        </div>
      )}
      {/* Latest offers */}
      <div className="px-4 mt-8">
        <div className="flex items-center justify-between mb-4">
          <h5 className="text-lg font-light text-text">
            {t("latestOffers")}
          </h5>
          <Link
            to="/app/offers"
            className="text-primary text-sm font-medium flex items-center gap-1"
          >
            {t("actions.viewAll")}
            <CaretRight size={16} />
          </Link>
        </div>

        <div className="space-y-3">
          {latestOffers === undefined ? (
            // Loading skeleton
            (<>
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4 p-4 bg-white rounded-xl animate-pulse">
                  <div className="w-14 h-14 bg-gray-200 rounded-xl" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </div>
                  <div className="w-12 h-12 bg-gray-200 rounded-lg" />
                </div>
              ))}
            </>)
          ) : latestOffers.length === 0 ? (
            <p className="text-center text-text-muted py-4">
              {currentLang === "ro" ? "Nu există oferte momentan" : "No offers available"}
            </p>
          ) : (
            latestOffers.map((offer) => {
              const desc = offer.shortDescription || offer.description;
              const description = typeof desc === "string" ? desc : (desc?.ro || desc?.en || "");
              const benefit = offer.benefitText;
              const benefitText = typeof benefit === "string" ? benefit : (benefit?.ro || benefit?.en || "Beneficiu");
              
              return (
                <OfferCard
                  key={offer._id}
                  offer={{
                    id: offer._id,
                    partner: offer.partner?.name || "Partner",
                    description,
                    discount: offer.discountType === "percent"
                      ? `${offer.discountValue}%`
                      : benefitText,
                    category: offer.category?.name || "",
                    imageUrl: offer.imageUrl,
                  }}
                />
              );
            })
          )}
        </div>
      </div>
      {/* PWA Install Banner */}
      <PWAInstallBanner />
    </div>
  )
}

// Componenta Quick Action
function QuickAction({
  icon,
  label,
  to,
  badge,
}: {
  icon: React.ReactNode;
  label: string;
  to: string;
  badge?: number;
}) {
  return (
    <Link
      to={to as any}
      className="flex flex-col items-center gap-2 p-3 bg-white/100 rounded-xl hover:shadow-md transition-shadow relative"
    >
      <div className="w-20h-12 rounded-full flex items-center justify-center text-primary">
        {icon}
      </div>
      <span className="text-sm text-primary font-medium">{label}</span>
      {badge !== undefined && badge > 0 && (
        <span className="w-5 h-5 absolute -top-1 -right-1 bg-white text-primary font-bold rounded-full flex items-center justify-center">
          {badge}
        </span>
      )}
    </Link>
  );
}

// Componenta Offer Card
function OfferCard({
  offer,
}: {
  offer: {
    id: string;
    partner: string;
    description: string;
    discount: string;
    category: string;
    imageUrl?: string | null;
  };
}) {
  return (
    <Link
      to="/app/offers/$offerId"
      params={{ offerId: offer.id }}
      className="flex items-center gap-4 "
    >
      <div className="w-24 h-24 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
        {offer.imageUrl ? (
          <img
            src={offer.imageUrl}
            alt={offer.partner}
            className="w-full h-full object-cover"
          />
        ) : (
          <Percent size={20} className="text-primary" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-text truncate">{offer.partner}</p>
       
        <p className="text-xs text-text-muted mt-1">{offer.category} • {offer.discount}</p>
        <span className="inline-block px-2 py-1 bg-primary/5 text-primary text-xs font-bold rounded mt-2 whitespace-pre-line break-words">
          {offer.description}
        </span>
      </div>
    </Link>
  );
}
